import React, { useEffect, useState, useRef } from 'react';
import { MotifDropdown, MotifIcon, MotifBadge, MotifChip, MotifTooltip } from '@ey-xd/motif-react';
import {
  navigationIcArrowDropDown24px,
  navigationIcClose24px,
} from '@ey-xd/motif-react/assets/icons';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  Current_User,
  Is_Read_Only_State,
  Recoil_Quarter_State,
  Recoil_Selected_Account_State,
  Flattened_Account_List_State,
} from '@recoil/recoil';
import { logError } from '@util/commonFunctions';
import exitApp from '@assets/icons/exitApp.svg';
import iconAccount from '@assets/icons/gcsp_default.svg';
import iconPerson from '@assets/icons/pl_default.svg';
import iconAccountSelected from '@assets/icons/gcsp_hover.svg';
import iconPersonSelected from '@assets/icons/pl_hover.svg';
import iconReadOnlySelected from '@assets/icons/isReadOnlySelected.png';
import iconReadOnly from '@assets/icons/isReadOnly.png';
import iconMsl from '@assets/icons/msl_default.svg';
import iconMslSelected from '@assets/icons/msl_hover.svg';

import { useNavigate } from 'react-router-dom';
import { isMobile, isTablet } from 'react-device-detect';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import SpinnerLoader from '@atoms/SpinnerLoader';
import { getLogOutStatus } from '@services/commonServices';
import { LOGOUT_SCREEN, ROLE_CONST } from '@constants/commonConstants';
import {
  DropdownDisplayContainer,
  DropdownIconContainer,
  DropdownTextContainer,
  DropdownDueContainer,
  DropdownContainer,
  UserIconContainer,
  UserTextContainer,
  DueTextContainer,
  StyledDropdownItem,
  MobileUserProfileButton,
  PagesText,
  CloseIconContainer,
  DesktopUserListContainer,
  MobileUserListContainer,
} from './styles/userProfileDropdownStyles';
import { isMslDelegate } from '../../../util/commonFunctions';

const UserProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHandleToggleJustUsed, setIsHandleToggleJustUsed] = useState(null);
  const [notForecastedAccountCount, setNotForecastedAccountCount] = useState(0); // eslint-disable-line
  const [, setCurrentUser] = useRecoilState(Current_User);

  const currentUser = useRecoilValue(Current_User);
  const quarterData = useRecoilValue(Recoil_Quarter_State);
  const [selectedUser, setSelectedUser] = useRecoilState(Recoil_Selected_Account_State);
  const setIsReadOnlyState = useSetRecoilState(Is_Read_Only_State);
  const flattenedAccountList = useRecoilValue(Flattened_Account_List_State);
  const navigate = useNavigate();

  useEffect(() => {
    if (flattenedAccountList !== null) {
      let notDoneCount = 0;

      setIsLoading(false);
      flattenedAccountList?.forEach(role => {
        const roleForecastDetails = role?.dueDetails;
        if (roleForecastDetails !== null && roleForecastDetails !== undefined) {
          if (roleForecastDetails.chipTitle === 'Submitted') {
            notDoneCount += 1;
            setNotForecastedAccountCount(notDoneCount);
          }
        }
      });
    }
  }, [flattenedAccountList]);

  const handleLogout = state => {
    getLogOutStatus(currentUser.email)
      .then(() => {
        localStorage.clear();
        sessionStorage.clear();
        navigate('/logout', {
          state: state,
        });
      })

      .catch(error => {
        logError('getLogOutStatus', error);
      });
  };

  const handleToggle = type => {
    let timeoutId;
    if (type === 'toggle') {
      setIsOpen(!isOpen);
      setIsHandleToggleJustUsed(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsHandleToggleJustUsed(null);
      }, 100);
    } else {
      if (isHandleToggleJustUsed === null) {
        setIsOpen(false);
        setIsHandleToggleJustUsed(null);
      }
    }
  };

  const getUserIcon = (account, isOnNavbar) => {
    if (account.role == 'PL') {
      if (isOnNavbar) return iconPerson;
      if (selectedUser && selectedUser.account_id === account.account_id) return iconPersonSelected;
      else return iconPerson;
    } else if ((account.role == 'GCSP' || account.role == 'MSL') && account.read_only) {
      if (isOnNavbar) return iconReadOnly;
      if (selectedUser && selectedUser.account_id === account.account_id) {
        return iconReadOnlySelected;
      } else return iconReadOnly;
    } else if (account.role == 'GCSP') {
      if (isOnNavbar) return iconAccount;
      if (selectedUser && selectedUser.account_id === account.account_id) {
        return iconAccountSelected;
      } else return iconAccount;
    } else if (account.role === 'MSL') {
      if (isOnNavbar) return iconMsl;
      if (selectedUser && selectedUser.account_id === account.account_id) {
        return iconMslSelected;
      } else return iconMsl;
    }
  };

  const getUserForecastDueInfo = dueDetails => {
    if (dueDetails == null) return;

    const chipStyle = {
      color: '#ffffff',
    };
    const shouldApplyStyle = dueDetails.chipType === 'outlined';

    return (
      <MotifChip
        variant={dueDetails.chipVariant}
        type={dueDetails.chipType}
        style={shouldApplyStyle ? chipStyle : undefined}
      >
        {dueDetails.chipTitle.split(',')[0].trim()}
      </MotifChip>
    );
  };

  const getNotForcastedCount = count => {
    return flattenedAccountList.filter(account => account.read_only === false).length - count;
  };

  const onDropdownUserClicked = user => {
    setSelectedUser(user);
    setIsOpen(false);
    setIsReadOnlyState(user?.read_only);
    localStorage.setItem('selectedUser', JSON.stringify(user));
    localStorage.setItem('isReadOnly', user?.read_only);
    const isDelegate = isMslDelegate(user);

    // Map read-only roles to their base roles for consistent routing
    let mappedRole = user?.role;
    if (mappedRole === ROLE_CONST.GCSP_READ_ONLY || mappedRole === 'GCSP READ ONLY') {
      mappedRole = ROLE_CONST.GCSP;
    } else if (mappedRole === ROLE_CONST.MSL_READ_ONLY || mappedRole === 'MSL READ ONLY') {
      mappedRole = ROLE_CONST.MSL;
    } else if (mappedRole === ROLE_CONST.MSL_DELEGATE || mappedRole === 'MSL DELEGATE') {
      mappedRole = ROLE_CONST.MSL;
    }

    const userVal = {
      email: localStorage.getItem('userEmail'),
      role: mappedRole,
      account_id: user?.account_id || user?.id,
    };
    setCurrentUser(userVal);
    if (user?.apiRole === ROLE_CONST.MSL_DELEGATE) {
      localStorage.setItem('mslDelegate', user?.parent_msl_id);
    } else localStorage.removeItem('mslDelegate');

    if (user?.read_only || isDelegate) navigate('/forecast');
    else navigate('/');

    window.location.reload();
  };

  const [ariaLabel, setAriaLabel] = useState('Count of Accounts');

  const announceCount = () => {
    const count = getNotForcastedCount(notForecastedAccountCount);
    const message = `count of accounts: ${count}`;
    setAriaLabel(message);
  };

  const handleLogoutButtonClick = () => {
    const modalState = {
      TITLE: LOGOUT_SCREEN.HEADER_TITLE,
      DESCRIPTION: LOGOUT_SCREEN.DESCRIPTION,
      BUTTON_TITLE: LOGOUT_SCREEN.BUTTON_TITLE,
    };
    handleLogout(modalState);
  };

  const DesktopUserDropdown = () => {
    const dropdownRef = useRef();
    const [focusedIndex, setFocusedIndex] = useState(-1);

    useEffect(() => {
      if (focusedIndex !== -1 && dropdownRef.current) {
        dropdownRef.current.children[focusedIndex].focus();
      }
    }, [focusedIndex]);

    const handleKeyDown = event => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex(prevIndex => {
            return prevIndex < flattenedAccountList.length - 1 ? prevIndex + 1 : prevIndex;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : 0));
          break;
        case 'Enter':
          if (focusedIndex !== -1) {
            onDropdownUserClicked(flattenedAccountList[focusedIndex]);
          }
          break;
        default:
          break;
      }
    };

    return (
      <>
        {flattenedAccountList != null &&
        currentUser != null &&
        quarterData != null &&
        selectedUser != null ? (
          <div className="motif-col-xs-1">
            <MotifDropdown
              ref={dropdownRef}
              open={isOpen}
              id="dropdown"
              aria-labelledby="dropdown-trigger"
              style={{ marginRight: '12px' }}
              handleClickOutside={() => {
                handleToggle('outsideClick');
              }}
              trigger={
                <>
                  <DropdownDisplayContainer
                    onClick={() => {
                      handleToggle('toggle');
                    }}
                    onKeyDown={handleKeyDown}
                    tabIndex="0"
                    id="dropdown-trigger"
                  >
                    <DropdownIconContainer>
                      {' '}
                      <img
                        src={getUserIcon(selectedUser, true)}
                        alt=""
                        style={{ width: '16px', height: '16px' }}
                      />
                    </DropdownIconContainer>

                    <DropdownTextContainer>
                      {selectedUser.accountName.length < 20
                        ? selectedUser.accountName
                        : `${selectedUser.accountName.slice(0, 20)}...`}
                    </DropdownTextContainer>

                    <DropdownDueContainer>
                      <MotifBadge
                        variant="error"
                        value={getNotForcastedCount(notForecastedAccountCount)}
                        style={{
                          width: '19px',
                          height: '19px',
                          padding: '2px 5px 2px 5px',
                          borderRadius: '12px',
                          gap: '6px',
                          color: '#1A1A24',
                          backgroundColor: '#F95D54',
                        }}
                        aria-label={ariaLabel}
                        onMouseOver={announceCount}
                      >
                        <span aria-hidden="true">
                          {getNotForcastedCount(notForecastedAccountCount)}
                        </span>
                      </MotifBadge>
                      <MotifIcon src={navigationIcArrowDropDown24px} />
                    </DropdownDueContainer>
                  </DropdownDisplayContainer>
                </>
              }
            >
              <DesktopUserListContainer>
                {/* Edit Account List */}
                {flattenedAccountList
                  .filter(account => account.read_only === false && !account.parent_msl_id)
                  .map((account, index) => (
                    <StyledDropdownItem
                      key={index}
                      onClick={() => onDropdownUserClicked(account)}
                      selected={
                        selectedUser ? selectedUser.account_id === account.account_id : null
                      }
                    >
                      <DropdownContainer>
                        <UserIconContainer>
                          <img
                            src={getUserIcon(account)}
                            alt=""
                            style={{ width: '16px', height: '16px' }}
                          />
                        </UserIconContainer>
                        <UserTextContainer
                          selected={
                            selectedUser ? selectedUser.account_id === account.account_id : null
                          }
                        >
                          <MotifTooltip
                            id="bottom"
                            trigger={
                              account.accountName.length < 13
                                ? account.accountName
                                : `${account.accountName.slice(0, 13)}...`
                            }
                            allowHover={true}
                            allowClick={true}
                            placement="top"
                          >
                            {account.accountName}
                          </MotifTooltip>
                        </UserTextContainer>
                        <DueTextContainer>
                          {getUserForecastDueInfo(account.dueDetails)}
                        </DueTextContainer>
                      </DropdownContainer>
                    </StyledDropdownItem>
                  ))}

                {/* MSL Delegate Access: Text */}
                {flattenedAccountList
                  .filter(account => account.parent_msl_id)
                  .map((account, index) => (
                    <div key={index}>
                      {index === 0 ? (
                        <StyledDropdownItem
                          onClick={() => {
                            return; // eslint-disable-line
                          }}
                        >
                          <UserTextContainer style={{ marginLeft: '5px' }}>
                            Delegate Access:
                          </UserTextContainer>
                        </StyledDropdownItem>
                      ) : null}
                    </div>
                  ))}

                {/* MSL Delegate Accounts List */}
                {flattenedAccountList
                  .filter(account => account.parent_msl_id)
                  .map((account, index) => (
                    // <>
                    <StyledDropdownItem
                      key={index}
                      onClick={() => onDropdownUserClicked(account)}
                      selected={
                        selectedUser ? selectedUser.account_id === account.account_id : null
                      }
                    >
                      <DropdownContainer>
                        <UserIconContainer>
                          <img
                            src={getUserIcon(account)}
                            alt=""
                            style={{ width: '16px', height: '16px' }}
                          />
                        </UserIconContainer>
                        <UserTextContainer
                          selected={
                            selectedUser ? selectedUser.account_id === account.account_id : null
                          }
                        >
                          <MotifTooltip
                            id="bottom"
                            trigger={
                              account.accountName.length < 13
                                ? account.accountName
                                : `${account.accountName.slice(0, 13)}...`
                            }
                            allowHover={true}
                            allowClick={true}
                            placement="top"
                          >
                            {account.accountName}
                          </MotifTooltip>
                        </UserTextContainer>
                        <DueTextContainer>
                          {getUserForecastDueInfo(account.dueDetails)}
                        </DueTextContainer>
                      </DropdownContainer>
                    </StyledDropdownItem>
                    // </>
                  ))}

                {/* Read-Only Access: Text */}
                {flattenedAccountList
                  .filter(account => account.read_only === true)
                  .map((account, index) => (
                    <div key={index}>
                      {index === 0 ? (
                        <StyledDropdownItem
                          onClick={() => {
                            return; // eslint-disable-line
                          }}
                        >
                          <UserTextContainer style={{ marginLeft: '5px' }}>
                            Read-Only Access:
                          </UserTextContainer>
                        </StyledDropdownItem>
                      ) : null}
                    </div>
                  ))}

                {/* Read Only Accounts List */}
                {flattenedAccountList
                  .filter(account => account.read_only === true)
                  .map((account, index) => (
                    // <>
                    <StyledDropdownItem
                      key={index}
                      onClick={() => onDropdownUserClicked(account)}
                      selected={
                        selectedUser ? selectedUser.account_id === account.account_id : null
                      }
                    >
                      <DropdownContainer>
                        <UserIconContainer>
                          <img
                            src={getUserIcon(account)}
                            alt=""
                            style={{ width: '16px', height: '16px' }}
                          />
                        </UserIconContainer>
                        <UserTextContainer
                          selected={
                            selectedUser ? selectedUser.account_id === account.account_id : null
                          }
                        >
                          <MotifTooltip
                            id="bottom"
                            trigger={
                              account.accountName.length < 30
                                ? account.accountName
                                : `${account.accountName.slice(0, 30)}...`
                            }
                            allowHover={true}
                            allowClick={true}
                            placement="top"
                          >
                            {account.accountName}
                          </MotifTooltip>
                        </UserTextContainer>
                        <DueTextContainer>
                          {getUserForecastDueInfo(account.dueDetails)}
                        </DueTextContainer>
                      </DropdownContainer>
                    </StyledDropdownItem>
                    // </>
                  ))}
              </DesktopUserListContainer>
              <StyledDropdownItem onClick={handleLogoutButtonClick}>
                <UserIconContainer style={{ marginRight: '10px' }}>
                  <img src={exitApp} alt="" style={{ width: '16px', height: '16px' }} />
                </UserIconContainer>
                <UserTextContainer style={{ marginLeft: '5px' }}>LOGOFF</UserTextContainer>
              </StyledDropdownItem>
            </MotifDropdown>
          </div>
        ) : null}
      </>
    );
  };

  const toggleDrawer = () => {
    setIsUserDropdownOpen(isUserDropdownOpen => !isUserDropdownOpen);
  };

  const MobileUserDropdown = () => {
    return (
      <>
        {' '}
        <MobileUserProfileButton src={iconPerson} onClick={toggleDrawer} />
        <Drawer
          className="userprofile-drawer"
          duration={250}
          onClose={toggleDrawer}
          direction="right"
          open={isUserDropdownOpen}
          overlayOpacity="0.6"
          zIndex={100}
          size={isTablet ? '40vw' : '80vw'}
          style={{ backgroundColor: '#1a1a24' }}
        >
          {isLoading ? (
            <div style={{ position: 'fixed', top: '50%', left: '50%' }}>
              <SpinnerLoader color="#ffffff" />
            </div>
          ) : (
            <>
              <CloseIconContainer>
                <MotifIcon
                  src={navigationIcClose24px}
                  style={{ position: 'absolute', right: '10px', top: '10px' }}
                  onClick={toggleDrawer}
                />
              </CloseIconContainer>
              {flattenedAccountList != null &&
              currentUser != null &&
              quarterData != null &&
              selectedUser != null ? (
                <>
                  <PagesText>SWITCH ROLE</PagesText>

                  <MobileUserListContainer>
                    {flattenedAccountList
                      .filter(account => account.read_only === false && !account.parent_msl_id)

                      .map((account, index) => (
                        <StyledDropdownItem
                          key={index}
                          // style={{ backgroundColor: '#2e2e38' }}
                          onClick={() => onDropdownUserClicked(account)}
                          selected={
                            selectedUser ? selectedUser.account_id === account.account_id : null
                          }
                        >
                          <DropdownContainer style={{ width: '100%' }}>
                            <UserIconContainer>
                              <img
                                src={getUserIcon(account)}
                                alt=""
                                style={{ width: '16px', height: '16px' }}
                              />
                            </UserIconContainer>
                            <UserTextContainer
                              selected={
                                selectedUser ? selectedUser.account_id === account.account_id : null
                              }
                            >
                              {account.accountName.length < 13
                                ? account.accountName
                                : `${account.accountName.slice(0, 13)}...`}
                            </UserTextContainer>
                            <DueTextContainer>
                              {getUserForecastDueInfo(account.dueDetails)}
                            </DueTextContainer>
                          </DropdownContainer>
                        </StyledDropdownItem>
                      ))}

                    {/* MSL Delegate Access: Text */}
                    {flattenedAccountList
                      .filter(account => account.parent_msl_id)
                      .map((account, index) => (
                        <div key={index}>
                          {index === 0 ? (
                            <StyledDropdownItem>
                              <UserTextContainer style={{ marginLeft: '5px' }}>
                                Delegate Access:
                              </UserTextContainer>
                            </StyledDropdownItem>
                          ) : null}
                        </div>
                      ))}

                    {/* MSL Delegate Accounts List */}
                    {flattenedAccountList
                      .filter(account => account.parent_msl_id)
                      .map((account, index) => (
                        // <>
                        <StyledDropdownItem
                          key={index}
                          onClick={() => onDropdownUserClicked(account)}
                          selected={
                            selectedUser ? selectedUser.account_id === account.account_id : null
                          }
                        >
                          <DropdownContainer>
                            <UserIconContainer>
                              <img
                                src={getUserIcon(account)}
                                alt=""
                                style={{ width: '16px', height: '16px' }}
                              />
                            </UserIconContainer>
                            <UserTextContainer
                              selected={
                                selectedUser ? selectedUser.account_id === account.account_id : null
                              }
                            >
                              <MotifTooltip
                                id="bottom"
                                trigger={
                                  account.accountName.length < 13
                                    ? account.accountName
                                    : `${account.accountName.slice(0, 13)}...`
                                }
                                allowHover={true}
                                allowClick={true}
                                placement="top"
                              >
                                {account.accountName}
                              </MotifTooltip>
                            </UserTextContainer>
                            <DueTextContainer>
                              {getUserForecastDueInfo(account.dueDetails)}
                            </DueTextContainer>
                          </DropdownContainer>
                        </StyledDropdownItem>
                        // </>
                      ))}

                    {/* Read-Only Access: Text */}
                    {flattenedAccountList
                      .filter(account => account.read_only === true)
                      .map((account, index) => (
                        <div key={index}>
                          {index === 0 ? (
                            <StyledDropdownItem>
                              <UserTextContainer style={{ marginLeft: '5px' }}>
                                Read-Only Access:
                              </UserTextContainer>
                            </StyledDropdownItem>
                          ) : null}
                        </div>
                      ))}

                    {flattenedAccountList
                      .filter(account => account.read_only === true)
                      .map((account, index) => (
                        <StyledDropdownItem
                          key={index}
                          onClick={() => onDropdownUserClicked(account)}
                          selected={
                            selectedUser ? selectedUser.account_id === account.account_id : null
                          }
                        >
                          <DropdownContainer style={{ width: '100%' }}>
                            <UserIconContainer>
                              <img
                                src={getUserIcon(account)}
                                alt=""
                                style={{ width: '16px', height: '16px' }}
                              />
                            </UserIconContainer>
                            <UserTextContainer
                              selected={
                                selectedUser ? selectedUser.account_id === account.account_id : null
                              }
                            >
                              {account.accountName.length < 30
                                ? account.accountName
                                : `${account.accountName.slice(0, 30)}...`}
                            </UserTextContainer>
                            <DueTextContainer>
                              {getUserForecastDueInfo(account.dueDetails)}
                            </DueTextContainer>
                          </DropdownContainer>
                        </StyledDropdownItem>
                      ))}
                  </MobileUserListContainer>
                  <StyledDropdownItem
                    onClick={handleLogoutButtonClick}
                    style={{ position: 'fixed', bottom: '0' }}
                  >
                    <DropdownContainer style={{ width: '100%' }}>
                      <UserIconContainer>
                        <img src={exitApp} alt="" style={{ width: '16px', height: '16px' }} />
                      </UserIconContainer>
                      <UserTextContainer>LOGOFF</UserTextContainer>
                      <DueTextContainer />
                    </DropdownContainer>
                  </StyledDropdownItem>
                </>
              ) : null}
            </>
          )}
        </Drawer>
      </>
    );
  };

  return <>{isMobile ? <MobileUserDropdown /> : <DesktopUserDropdown />}</>;
};

export default UserProfileDropdown;
